import { Component, OnInit, ChangeDetectorRef, ViewChild, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { StatusPipe } from '@src/app/shared/pipes/status.pipe';
import { ConfirmationService } from 'primeng/api';
import { CountryTransformPipe } from '@src/app/shared/pipes/country.pipe';
import { CustomersService, SalesService, CustomerDetailsResponse } from '@src/app/shared/backend/clients';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { UserRolesService } from '@src/app/shared/services/userRoles.service';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { DialogReasonComponent } from '@src/app/shared/components/dialog-reason/dialog-reason.component';
import { FilterOperator } from '@src/app/shared/enums/FilterOperator.enum';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

@Component({
    selector: 'app-view-customer',
    templateUrl: './view-customer.component.html',
    styleUrl: './view-customer.component.scss',
    host: {
        class: 'app-view-customer'
    },
    providers: [StatusPipe, ConfirmationService, UserRolesService, CountryTransformPipe],
    standalone: false
})
export class ViewCustomerComponent extends BaseViewComponent implements OnInit, OnDestroy {

  private readonly destroyedComponent$ = new Subject<void>();

  @ViewChild('dialog') dialog: DialogReasonComponent | undefined;

  salesData: any;
  customerRetailers: any;
  delete: boolean = false;
  allowModifyCustomer: boolean = false;
  allowViewSale: boolean = false;
  customerId: any;

  protected pageSize: number = 10;
  protected pageNumber: number = 1;
  protected totalRecords: any = 0;

  customer: CustomerDetailsResponse = {
    id: 0,
    acceptedTerms: false,
    acceptedPrivacyPolicy: false,
    isAnonymised: false,
    createdOn: ''
  };
  filterOperator = FilterOperator;

  constructor(
    private permissionService: PermissionService,
    private router: Router,
    private route: ActivatedRoute,
    private customersService: CustomersService,
    private salesService: SalesService,
    private changeDetector: ChangeDetectorRef) { super() }


  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {

    this.route.params
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(async (params) => {
        this.customerId = Number(params['customerId']);
        if (this.customerId > 0) {
          await this.get();
        }
      });

    this.delete = await this.permissionService.hasActionPermissions(Actions.delete);
    this.allowModifyCustomer = await this.permissionService.hasActionPermissions(Actions.modify_customers);
    this.allowViewSale = await this.permissionService.hasActionPermissions(Actions.view_sales);
  }

  override async get() {
    try {
      this.customer = await firstValueFrom(this.customersService.apiCustomersDetailsIdGet(this.customerId));
      this.customerRetailers = this.customer.customerRetailers;
      const filters: any[] = [];
      filters.push({
        propertyName: 'customerId',
        operator: this.filterOperator.EQUALS,
        values: [this.customerId.toString()]
      });

      const filter = {
        "pageNumber": this.pageNumber,
        "pageSize": this.pageSize,
        "filters": filters,
        "sort": { propertyName: "date", type: "desc" }
      };

      const result = await firstValueFrom(this.salesService.apiSalesFilterTablePost(filter));

      this.salesData = result.results;
      this.totalRecords = result.totalCount;

      this.cdr.detectChanges();

    } catch (error) {
      console.log('Error fetching customer data:', error);
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  async onPageChange(event: any) {
    this.pageNumber = event.page;
    this.pageSize = event.rows;
    await this.setData(this.customerId);
  }

  goToEditCustomer() {
    this.router.navigate([`/customers/${this.customerId}/edit`]);
  }

  async deleteCustomer(doDelete: boolean): Promise<void> {
    if (doDelete) {
      await firstValueFrom(this.customersService.apiCustomersDeleteIdDelete(this.customerId));
      this.router.navigate([`/customers`]);
    }
  }

  ngOnDestroy(): void {
    this.destroyedComponent$.next();
    this.destroyedComponent$.complete();
  }
}
